// components/tenant-enrollment/domain-setup.tsx
'use client';

import React, { useState, useEffect } from 'react';
import { Building2, ArrowRight, AlertCircle, CheckCircle, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTenantEnrollmentService } from '@/lib/use-generated-lib';
import { useTheme } from '@/lib/theme-context';
import { getBackgroundClasses, getTextClasses, getBorderClasses, getInputClasses } from '@/lib/theme-utils';

interface DomainSetupProps {
  onComplete: (domain: string, organizationName: string) => void;
  isLoading?: boolean;
  prefillDomain?: string;
}

export function DomainSetup({ onComplete, isLoading, prefillDomain = '' }: DomainSetupProps) {
  const { theme } = useTheme();
  const [domain, setDomain] = useState(prefillDomain);
  const [organizationName, setOrganizationName] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDomainValid, setIsDomainValid] = useState(false);
  const [isOrganizationNameValid, setIsOrganizationNameValid] = useState(false);
  const { tenantEnrollmentService, isLoaded: tenantEnrollmentServiceLoaded } = useTenantEnrollmentService();

  // Update domain validation when prefillDomain changes
  useEffect(() => {
    if (prefillDomain) {
      handleDomainChange(prefillDomain);
    }
  }, [prefillDomain]);

  const validateDomain = (domain: string) => {
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
    return domainRegex.test(domain);
  };

  const isCommercialDomain = (domain: string) => {
    const commercialDomains = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
      'icloud.com', 'protonmail.com', 'tutanota.com', 'mail.com'
    ];
    return commercialDomains.includes(domain.toLowerCase());
  };

  const validateOrganizationName = (name: string) => {
    return name.trim().length >= 2 && name.trim().length <= 100;
  };

  const handleDomainChange = (value: string) => {
    setDomain(value);
    setError(null);
    const isValid = validateDomain(value);
    setIsDomainValid(isValid);
    
    if (isValid && isCommercialDomain(value)) {
      setError('Commercial email domains are not allowed. Please use your organization\'s domain.');
    }
  };

  const handleOrganizationNameChange = (value: string) => {
    setOrganizationName(value);
    setError(null);
    setIsOrganizationNameValid(validateOrganizationName(value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!domain || !organizationName) {
      setError('Please fill in all fields');
      return;
    }

    if (!isDomainValid) {
      setError('Please enter a valid domain name');
      return;
    }

    if (!isOrganizationNameValid) {
      setError('Organization name must be between 2 and 100 characters');
      return;
    }

    if (isCommercialDomain(domain)) {
      setError('Commercial email domains are not allowed. Please use your organization\'s domain.');
      return;
    }

    if (!tenantEnrollmentService || !tenantEnrollmentServiceLoaded) {
      setError('Service not ready. Please try again.');
      return;
    }

    setIsChecking(true);
    setError(null);

    try {
      // Use TenantEnrollmentService to check domain availability
      const result = await tenantEnrollmentService.checkDomainAvailability({
        domain: domain
      });
      
      if (result.available) {
        onComplete(domain, organizationName.trim());
      } else {
        setError('This domain is already registered. Please contact support if you believe this is an error.');
      }
    } catch (error) {
      // Handle domain check errors gracefully
      let errorMessage = 'Unable to verify domain. Please try again.';
      
      if (error instanceof Error) {
        const errorMsg = error.message.toLowerCase();
        if (errorMsg.includes('network') || errorMsg.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (errorMsg.includes('server') || errorMsg.includes('500')) {
          errorMessage = 'Server error. Please try again in a moment.';
        } else if (errorMsg.includes('unauthorized') || errorMsg.includes('403')) {
          errorMessage = 'Domain verification service unavailable. Please try again.';
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-6">
        <div className="mx-auto w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mb-3">
          <Building2 className="w-6 h-6 text-white" />
        </div>
        <h2 className={`text-xl font-bold ${getTextClasses(theme)} mb-1`}>
          Set Up Your Organization
        </h2>
        <p className={`${getTextClasses(theme, 'muted')} text-sm`}>
          Enter your organization's details to get started with the setup process.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Organization Name */}
        <div className="space-y-1">
          <Label htmlFor="organizationName" className={`${getTextClasses(theme)} text-sm`}>
            Organization Name *
          </Label>
          <Input
            id="organizationName"
            type="text"
            value={organizationName}
            onChange={(e) => handleOrganizationNameChange(e.target.value)}
            placeholder="Acme Corporation"
            className={`${getInputClasses(theme)} h-10 ${
              organizationName && isOrganizationNameValid ? 'border-green-500' : organizationName && !isOrganizationNameValid ? 'border-red-500' : ''
            }`}
            disabled={isLoading || isChecking}
            required
          />
          <p className={`text-xs ${getTextClasses(theme, 'muted')}`}>
            The name of your organization as it will appear in the system
          </p>
        </div>

        {/* Domain */}
        <div className="space-y-1">
          <Label htmlFor="domain" className={`${getTextClasses(theme)} text-sm`}>
            Domain Name *
          </Label>
          <div className="relative">
            <Globe className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${getTextClasses(theme, 'muted')}`} />
            <Input
              id="domain"
              type="text"
              value={domain}
              onChange={(e) => handleDomainChange(e.target.value)}
              placeholder="acme.com"
              className={`pl-10 ${getInputClasses(theme)} pr-10 h-10 ${
                domain && isDomainValid && !isCommercialDomain(domain) ? 'border-green-500' : domain && (!isDomainValid || isCommercialDomain(domain)) ? 'border-red-500' : ''
              }`}
              disabled={isLoading || isChecking}
              required
            />
            {domain && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                {isDomainValid && !isCommercialDomain(domain) ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-red-500" />
                )}
              </div>
            )}
          </div>
          <p className={`text-xs ${getTextClasses(theme, 'muted')}`}>
            Enter your organization's primary domain (e.g., acme.com). Commercial domains like gmail.com are not allowed.
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
            <div className="flex items-center">
              <AlertCircle className="w-4 h-4 text-red-400 mr-2" />
              <p className="text-xs text-red-400">{error}</p>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={!domain || !organizationName || !isDomainValid || !isOrganizationNameValid || isCommercialDomain(domain) || isLoading || isChecking}
          className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white h-10"
        >
          {isChecking ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              Checking Domain...
            </>
          ) : (
            <>
              Continue
              <ArrowRight className="ml-2 w-4 h-4" />
            </>
          )}
        </Button>
      </form>

      {/* Help Text */}
      <div className={`mt-4 p-3 ${getBackgroundClasses(theme, 'card')} ${getBorderClasses(theme)} rounded-lg`}>
        <h4 className={`text-xs font-medium ${getTextClasses(theme)} mb-1`}>What happens next?</h4>
        <ul className={`text-xs ${getTextClasses(theme, 'muted')} space-y-0.5`}>
          <li>• We'll verify your domain is available</li>
          <li>• You'll set up your admin account</li>
          <li>• You can choose between local authentication or SSO</li>
          <li>• Your organization will be ready for users to sign in</li>
        </ul>
      </div>
    </div>
  );
}